import React, { useEffect, useMemo, useState } from 'react';
import Card from 'components/card';
import axios from 'axios';
import { CONFIG } from 'variables/config';
import { MdChevronLeft, MdChevronRight, MdSearch, MdClose, MdPlayArrow, MdDelete, MdOpenInNew, MdWarning } from 'react-icons/md';
import { allChannels } from 'variables/industries';
import { userDisplayName } from 'utils/userName';
import { textMatches } from 'utils/search';

type ApprovalStatus = 'pending' | 'conflict' | 'approved' | 'rejected';

interface Lead {
    _id: string;
    sellerId: {
        _id: string;
        name?: string;
        surname?: string;
        company?: string;
        email: string;
    } | null;
    orderNumber?: string;
    buyerPrice: number;
    leadPrice: number;
    email: string;
    status: string;
    approvalStatus?: ApprovalStatus;
    paidAt?: string;
    createdAt: string;
}

interface PurchaseOrder {
    _id: string;
    ownerId: {
        _id: string;
        name?: string;
        surname?: string;
        company?: string;
        email: string;
    } | null;
    price?: number; // Deprecated field
    maxPricePerLead?: number; // New field
    leadCount: number;
    industry: string;
    product: string;
    departments: string[];
    channels?: string[];
    mandatoryChannels?: string[];
    status: 'active' | 'completed' | 'cancelled' | 'pending_payment';
    isAutoSuspended?: boolean;
    totalBudget: number;
    leadsReceived: number;
    amountSpent: number;
    createdAt: string;
    leads: Lead[];
}

interface GroupedOrders {
    industry: string;
    product: string;
    orders: PurchaseOrder[];
}

type StatusFilter = 'all' | 'active' | 'completed' | 'cancelled' | 'pending_payment' | 'cap_atteint';
type LeadStatusFilter = 'all' | ApprovalStatus;
type LeadPaidFilter = 'all' | 'paid' | 'unpaid';
interface LeadFilter { status: LeadStatusFilter; paid: LeadPaidFilter; }

const PAGE_SIZE = 50;

const STATUS_LABELS: Record<PurchaseOrder['status'], string> = {
    active: 'Active',
    completed: 'Completed',
    cancelled: 'Cancelled',
    pending_payment: 'Pending Payment',
};

const leadApproval = (lead: Lead): ApprovalStatus => lead.approvalStatus || 'pending';

const countLeads = (order: PurchaseOrder) => {
    const counts: Record<ApprovalStatus, number> = { approved: 0, pending: 0, conflict: 0, rejected: 0 };
    (order.leads || []).forEach((l) => { counts[leadApproval(l)] += 1; });
    return counts;
};

// The back-office leads search matches a lead's Mongo _id, so this lands on that exact lead
// with all the admin actions (approve, reject, resolve conflict...).
const openLead = (leadId: string) => {
    window.open(`/back-office/leads?search=${leadId}`, '_blank', 'noopener');
};

export default function BackOfficePurchaseOrders() {
    const [orders, setOrders] = useState<PurchaseOrder[]>([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [statusFilter, setStatusFilter] = useState<StatusFilter>('active');
    const [searchInput, setSearchInput] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [expandedOrders, setExpandedOrders] = useState<Set<string>>(new Set());
    const [leadFilters, setLeadFilters] = useState<Record<string, LeadFilter>>({});

    const getLeadFilter = (orderId: string): LeadFilter =>
        leadFilters[orderId] ?? { status: 'all', paid: 'all' };

    const setLeadFilter = (orderId: string, patch: Partial<LeadFilter>) =>
        setLeadFilters((prev) => ({ ...prev, [orderId]: { ...getLeadFilter(orderId), ...patch } }));

    // Debounce search input
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(searchInput);
            setPage(1);
        }, 300);
        return () => clearTimeout(timer);
    }, [searchInput]);

    useEffect(() => {
        fetchPurchaseOrders();
    }, []);

    const fetchPurchaseOrders = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${CONFIG.apiUrl}/admin/purchase-orders`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            const flat: PurchaseOrder[] = (response.data.groups as GroupedOrders[])
                .flatMap((group) => group.orders)
                .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
            setOrders(flat);
        } catch (error) {
            console.error('Error fetching purchase orders:', error);
        } finally {
            setLoading(false);
        }
    };

    const updateOrder = (orderId: string, patch: Partial<PurchaseOrder>) =>
        setOrders((prev) => prev.map((o) => (o._id === orderId ? { ...o, ...patch } : o)));

    const toggleOrder = (orderId: string) => {
        setExpandedOrders((prev) => {
            const next = new Set(prev);
            if (next.has(orderId)) next.delete(orderId);
            else next.add(orderId);
            return next;
        });
    };

    // Clicking a lead counter opens the order's leads, pre-filtered on that approval status.
    const showLeads = (orderId: string, status: LeadStatusFilter) => {
        setLeadFilter(orderId, { status });
        setExpandedOrders((prev) => new Set(prev).add(orderId));
    };

    const handleDeletePurchaseOrder = async (order: PurchaseOrder) => {
        const confirmed = window.confirm(`Delete this purchase order (${order.industry} - ${order.product})? This cannot be undone.`);
        if (!confirmed) return;
        try {
            const token = localStorage.getItem('token');
            await axios.delete(`${CONFIG.apiUrl}/admin/purchase-orders/${order._id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setOrders((prev) => prev.filter((o) => o._id !== order._id));
        } catch (error) {
            console.error('Error deleting purchase order:', error);
            alert('Failed to delete purchase order');
        }
    };

    const handleLiftCapAtteint = async (order: PurchaseOrder) => {
        if (!window.confirm('Relancer cette commande ?')) return;
        try {
            const token = localStorage.getItem('token');
            await axios.patch(
                `${CONFIG.apiUrl}/admin/purchase-orders/${order._id}`,
                { isAutoSuspended: false },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            updateOrder(order._id, { isAutoSuspended: false });
        } catch (error) {
            console.error('Error lifting cap atteint:', error);
            alert('Failed to lift cap atteint');
        }
    };

    const handleToggleMandatoryChannel = async (order: PurchaseOrder, channel: string) => {
        const current = order.mandatoryChannels || [];
        const next = current.includes(channel)
            ? current.filter((c) => c !== channel)
            : [...current, channel];
        try {
            const token = localStorage.getItem('token');
            await axios.patch(
                `${CONFIG.apiUrl}/admin/purchase-orders/${order._id}`,
                { mandatoryChannels: next },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            updateOrder(order._id, { mandatoryChannels: next });
        } catch (error) {
            console.error('Error updating mandatoryChannels:', error);
            alert('Failed to update mandatory channels');
        }
    };

    const handleChangeStatus = async (order: PurchaseOrder, newStatus: PurchaseOrder['status']) => {
        if (newStatus === order.status) return;
        try {
            const token = localStorage.getItem('token');
            await axios.patch(
                `${CONFIG.apiUrl}/admin/purchase-orders/${order._id}`,
                { status: newStatus },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            updateOrder(order._id, { status: newStatus });
        } catch (error) {
            console.error('Error updating purchase order status:', error);
            alert('Failed to update purchase order status');
        }
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'active':
                return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
            case 'completed':
                return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
            case 'cancelled':
                return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
            case 'pending_payment':
                return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
            default:
                return 'bg-gray-100 text-gray-600 dark:bg-navy-700 dark:text-gray-400';
        }
    };

    const getApprovalColor = (status: ApprovalStatus) => {
        switch (status) {
            case 'approved':
                return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
            case 'rejected':
                return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
            case 'conflict':
                return 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200';
            default:
                return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
        }
    };

    const filteredOrders = useMemo(() => {
        return orders.filter((o) => {
            if (statusFilter === 'cap_atteint') {
                if (!o.isAutoSuspended) return false;
            } else if (statusFilter !== 'all' && o.status !== statusFilter) {
                return false;
            }
            return textMatches(debouncedSearch, [
                o._id,
                o.industry,
                o.product,
                o.ownerId?.name,
                o.ownerId?.surname,
                o.ownerId?.company,
                o.ownerId?.email,
                ...(o.departments || []),
            ]);
        });
    }, [orders, statusFilter, debouncedSearch]);

    const capAtteintCount = useMemo(() => orders.filter((o) => o.isAutoSuspended).length, [orders]);

    const total = filteredOrders.length;
    const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
    const pagedOrders = filteredOrders.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
    const startItem = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
    const endItem = Math.min(page * PAGE_SIZE, total);

    const getPageNumbers = () => {
        const pages: (number | '...')[] = [];
        if (totalPages <= 7) {
            for (let i = 1; i <= totalPages; i++) pages.push(i);
        } else {
            pages.push(1);
            if (page > 3) pages.push('...');
            for (let i = Math.max(2, page - 1); i <= Math.min(totalPages - 1, page + 1); i++) {
                pages.push(i);
            }
            if (page < totalPages - 2) pages.push('...');
            pages.push(totalPages);
        }
        return pages;
    };

    const renderLeadsPanel = (order: PurchaseOrder) => {
        const lf = getLeadFilter(order._id);
        const leads = order.leads || [];
        const filtered = leads.filter((l) => {
            if (lf.status !== 'all' && leadApproval(l) !== lf.status) return false;
            if (lf.paid === 'paid' && !l.paidAt) return false;
            if (lf.paid === 'unpaid' && l.paidAt) return false;
            return true;
        });

        return (
            <div className="flex flex-col gap-4">
                {/* Channels */}
                <div className="flex flex-col gap-3">
                    {order.channels && order.channels.length > 0 && (
                        <div>
                            <div className="mb-1 text-xs font-bold uppercase tracking-wide text-gray-500 dark:text-gray-400">Canaux</div>
                            <div className="text-sm text-navy-700 dark:text-white">{order.channels.join(', ')}</div>
                        </div>
                    )}
                    <div>
                        <div className="mb-1 text-xs font-bold uppercase tracking-wide text-gray-500 dark:text-gray-400">Canaux obligatoires</div>
                        <p className="mb-2 text-xs text-gray-500 dark:text-gray-400">
                            Si défini, seuls les vendeurs disposant d'un canal d'acquisition correspondant pourront recevoir des leads pour cette commande.
                        </p>
                        <div className="flex flex-wrap gap-2">
                            {allChannels.map((channel) => {
                                const isSelected = (order.mandatoryChannels || []).includes(channel);
                                return (
                                    <button
                                        key={channel}
                                        type="button"
                                        onClick={() => handleToggleMandatoryChannel(order, channel)}
                                        className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors ${
                                            isSelected
                                                ? 'bg-brand-500 text-white'
                                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-navy-700 dark:text-gray-300 dark:hover:bg-navy-600'
                                        }`}
                                    >
                                        {channel}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                </div>

                {/* Leads */}
                {leads.length === 0 ? (
                    <div className="py-2 text-center text-sm text-gray-500">No leads received yet</div>
                ) : (
                    <div>
                        <div className="mb-3 flex flex-wrap items-center gap-3">
                            <div className="font-semibold text-navy-700 dark:text-white">
                                Leads ({filtered.length}{filtered.length !== leads.length ? ` / ${leads.length}` : ''})
                            </div>
                            <div className="flex items-center gap-1">
                                <span className="text-xs font-semibold text-gray-500 dark:text-gray-400">Status:</span>
                                {(['all', 'pending', 'conflict', 'approved', 'rejected'] as LeadStatusFilter[]).map((s) => (
                                    <button
                                        key={s}
                                        onClick={() => setLeadFilter(order._id, { status: s })}
                                        className={`rounded px-2 py-0.5 text-[11px] font-semibold transition-colors ${
                                            lf.status === s
                                                ? s === 'approved' ? 'bg-green-500 text-white'
                                                : s === 'rejected' ? 'bg-red-500 text-white'
                                                : s === 'conflict' ? 'bg-purple-500 text-white'
                                                : s === 'pending' ? 'bg-yellow-400 text-white'
                                                : 'bg-brand-500 text-white'
                                                : 'bg-gray-200 text-gray-600 hover:bg-gray-300 dark:bg-navy-700 dark:text-gray-300 dark:hover:bg-navy-600'
                                        }`}
                                    >
                                        {s === 'all' ? 'All' : s.charAt(0).toUpperCase() + s.slice(1)}
                                    </button>
                                ))}
                            </div>
                            <div className="flex items-center gap-1">
                                <span className="text-xs font-semibold text-gray-500 dark:text-gray-400">Paid:</span>
                                {(['all', 'paid', 'unpaid'] as LeadPaidFilter[]).map((p) => (
                                    <button
                                        key={p}
                                        onClick={() => setLeadFilter(order._id, { paid: p })}
                                        className={`rounded px-2 py-0.5 text-[11px] font-semibold transition-colors ${
                                            lf.paid === p
                                                ? p === 'paid' ? 'bg-blue-500 text-white'
                                                : p === 'unpaid' ? 'bg-orange-500 text-white'
                                                : 'bg-brand-500 text-white'
                                                : 'bg-gray-200 text-gray-600 hover:bg-gray-300 dark:bg-navy-700 dark:text-gray-300 dark:hover:bg-navy-600'
                                        }`}
                                    >
                                        {p === 'all' ? 'All' : p.charAt(0).toUpperCase() + p.slice(1)}
                                    </button>
                                ))}
                            </div>
                        </div>
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead>
                                    <tr className="border-b border-gray-200 dark:border-navy-600">
                                        <th className="pb-2 pr-4 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">Lead</th>
                                        <th className="pb-2 pr-4 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">Email</th>
                                        <th className="pb-2 pr-4 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">Seller</th>
                                        <th className="pb-2 pr-4 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">Prices</th>
                                        <th className="pb-2 pr-4 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">Approval</th>
                                        <th className="pb-2 pr-4 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">Paid</th>
                                        <th className="pb-2 pr-4 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">Date</th>
                                        <th className="pb-2 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400"></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filtered.length === 0 ? (
                                        <tr>
                                            <td colSpan={8} className="py-4 text-center text-sm text-gray-500">No leads match these filters</td>
                                        </tr>
                                    ) : filtered.map((lead) => {
                                        const commission = (lead.buyerPrice || 0) - (lead.leadPrice || 0);
                                        const approval = leadApproval(lead);
                                        return (
                                            <tr
                                                key={lead._id}
                                                onClick={() => openLead(lead._id)}
                                                title="Ouvrir le lead dans un nouvel onglet"
                                                className="cursor-pointer border-b border-gray-100 hover:bg-white dark:border-navy-800 dark:hover:bg-navy-800"
                                            >
                                                <td className="py-2 pr-4 font-mono text-xs text-navy-700 dark:text-white">
                                                    {lead.orderNumber || lead._id.slice(-8)}
                                                </td>
                                                <td className="py-2 pr-4 text-xs">{lead.email || 'N/A'}</td>
                                                <td className="py-2 pr-4 text-xs">
                                                    {lead.sellerId ? (
                                                        <>
                                                            <div className="font-medium">{userDisplayName(lead.sellerId)}</div>
                                                            <div className="text-[10px] text-gray-500">{lead.sellerId.email}</div>
                                                        </>
                                                    ) : (
                                                        <span className="text-gray-500">Unknown</span>
                                                    )}
                                                </td>
                                                <td className="py-2 pr-4 text-xs">
                                                    <div className="text-gray-600 dark:text-gray-400">
                                                        <span className="uppercase tracking-wide text-gray-400">S</span> {(lead.leadPrice || 0).toFixed(2)}€
                                                    </div>
                                                    <div className="text-gray-600 dark:text-gray-400">
                                                        <span className="uppercase tracking-wide text-gray-400">B</span> {(lead.buyerPrice || 0).toFixed(2)}€
                                                    </div>
                                                    <div className="font-semibold text-green-600 dark:text-green-400">+{commission.toFixed(2)}€</div>
                                                </td>
                                                <td className="py-2 pr-4 text-xs">
                                                    <span className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold ${getApprovalColor(approval)}`}>
                                                        {approval}
                                                    </span>
                                                </td>
                                                <td className="py-2 pr-4 text-xs">
                                                    <span className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold ${
                                                        lead.paidAt ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' : 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200'
                                                    }`}>
                                                        {lead.paidAt ? '✓ Paid' : 'Unpaid'}
                                                    </span>
                                                </td>
                                                <td className="py-2 pr-4 text-xs text-gray-500">
                                                    {new Date(lead.createdAt).toLocaleDateString('fr-FR')}
                                                </td>
                                                <td className="py-2 text-gray-400">
                                                    <MdOpenInNew size={16} />
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className="pt-[50px]">
            <Card extra="p-5">
                {/* Header */}
                <div className="mb-5 flex items-center justify-between gap-2">
                    <div>
                        <div className="text-2xl font-bold text-navy-700 dark:text-white">
                            Purchase Orders
                        </div>
                        <div className="text-sm text-gray-600 dark:text-gray-400">
                            {orders.length.toLocaleString()} total purchase orders
                        </div>
                    </div>
                </div>

                {/* Filters */}
                <div className="mb-5 flex flex-col gap-3">
                    <div className="relative">
                        <MdSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                        <input
                            type="text"
                            placeholder="Search by buyer, industry, product, department, id..."
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                            className="w-full rounded-lg border border-gray-300 bg-white py-2 pl-9 pr-9 text-sm focus:border-brand-500 focus:outline-none dark:border-navy-600 dark:bg-navy-800 dark:text-white dark:placeholder-gray-500"
                        />
                        {searchInput && (
                            <button
                                onClick={() => setSearchInput('')}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                            >
                                <MdClose size={16} />
                            </button>
                        )}
                    </div>

                    {/* Status pills */}
                    <div className="flex flex-wrap gap-2">
                        {([
                            { value: 'all', label: 'All' },
                            { value: 'active', label: 'Active' },
                            { value: 'pending_payment', label: 'Pending Payment' },
                            { value: 'completed', label: 'Completed' },
                            { value: 'cancelled', label: 'Cancelled' },
                            { value: 'cap_atteint', label: `⏸ Cap atteint (${capAtteintCount})` },
                        ] as { value: StatusFilter; label: string }[]).map((s) => (
                            <button
                                key={s.value}
                                onClick={() => { setStatusFilter(s.value); setPage(1); }}
                                className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                                    statusFilter === s.value
                                        ? 'bg-navy-700 text-white dark:bg-brand-400'
                                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-navy-700 dark:text-gray-300 dark:hover:bg-navy-600'
                                }`}
                            >
                                {s.label}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Table */}
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead>
                            <tr className="border-b border-gray-200 dark:border-navy-600">
                                <th className="pb-3 pr-4 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">Order</th>
                                <th className="pb-3 pr-4 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">Industry</th>
                                <th className="pb-3 pr-4 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">Buyer</th>
                                <th className="pb-3 pr-4 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">Price / Budget</th>
                                <th className="pb-3 pr-4 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">Leads</th>
                                <th className="pb-3 pr-4 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">Status</th>
                                <th className="pb-3 pr-4 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">Date</th>
                                <th className="pb-3 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan={8} className="py-8 text-center text-sm text-gray-500">Loading...</td>
                                </tr>
                            ) : pagedOrders.length === 0 ? (
                                <tr>
                                    <td colSpan={8} className="py-8 text-center text-sm text-gray-500">No purchase orders found</td>
                                </tr>
                            ) : (
                                pagedOrders.map((order) => {
                                    const isExpanded = expandedOrders.has(order._id);
                                    const counts = countLeads(order);
                                    const remaining = Math.max(0, order.leadCount - counts.approved);
                                    const blocking = counts.pending + counts.conflict;
                                    const activeLeadFilter = isExpanded ? getLeadFilter(order._id).status : null;

                                    const counter = (status: ApprovalStatus, label: string, className: string, suffix = '') => (
                                        <button
                                            type="button"
                                            onClick={(e) => { e.stopPropagation(); showLeads(order._id, status); }}
                                            disabled={counts[status] === 0}
                                            className={`text-left hover:underline disabled:cursor-default disabled:no-underline disabled:opacity-60 ${className} ${
                                                activeLeadFilter === status ? 'underline' : ''
                                            }`}
                                        >
                                            {label}: {counts[status]}{suffix}
                                        </button>
                                    );

                                    return (
                                        <React.Fragment key={order._id}>
                                            <tr
                                                className={`cursor-pointer border-b border-gray-100 hover:bg-gray-50 dark:border-navy-700 dark:hover:bg-navy-700 ${
                                                    isExpanded ? 'bg-gray-50 dark:bg-navy-700' : ''
                                                }`}
                                                onClick={() => toggleOrder(order._id)}
                                            >
                                                {/* Order id + departments */}
                                                <td className="py-3 pr-4">
                                                    <div className="font-mono text-xs text-navy-700 dark:text-white">{order._id.slice(-8)}</div>
                                                    <div
                                                        className="max-w-[120px] truncate text-xs text-gray-500 dark:text-gray-400"
                                                        title={order.departments.join(', ')}
                                                    >
                                                        {order.departments.length === 1
                                                            ? order.departments[0]
                                                            : `${order.departments.length} depts`}
                                                    </div>
                                                </td>

                                                {/* Industry */}
                                                <td className="py-3 pr-4 text-sm">
                                                    <div className="font-medium text-navy-700 dark:text-white">{order.industry || '-'}</div>
                                                    {order.product && <div className="text-xs text-gray-500">{order.product}</div>}
                                                </td>

                                                {/* Buyer */}
                                                <td className="py-3 pr-4">
                                                    {order.ownerId ? (
                                                        <div>
                                                            <div className="text-sm font-medium text-navy-700 dark:text-white">
                                                                {userDisplayName(order.ownerId)}
                                                            </div>
                                                            <div className="text-xs text-gray-500">{order.ownerId.email}</div>
                                                        </div>
                                                    ) : (
                                                        <span className="text-xs text-gray-400 dark:text-gray-500">— Unknown buyer</span>
                                                    )}
                                                </td>

                                                {/* Price / budget */}
                                                <td className="py-3 pr-4 text-sm">
                                                    <div className="font-semibold text-brand-500">
                                                        {(order.maxPricePerLead || order.price || 0).toFixed(2)}€ <span className="text-xs font-normal text-gray-400">/ lead</span>
                                                    </div>
                                                    <div className="text-xs text-gray-600 dark:text-gray-400">
                                                        {order.leadCount?.toLocaleString()} leads · {order.totalBudget?.toLocaleString()}€
                                                    </div>
                                                </td>

                                                {/* Leads breakdown (each counter opens the matching leads) */}
                                                <td className="py-3 pr-4">
                                                    <div className="flex flex-col items-start gap-0.5 text-xs">
                                                        {counter('approved', 'Approved', 'font-semibold text-green-600 dark:text-green-400', ` / ${order.leadCount}`)}
                                                        {counter('pending', 'Pending', 'text-yellow-600 dark:text-yellow-400', ` / ${remaining}`)}
                                                        {counts.conflict > 0 && counter('conflict', 'Conflict', 'font-semibold text-purple-600 dark:text-purple-400')}
                                                        {counter('rejected', 'Rejected', 'text-red-500 dark:text-red-400')}
                                                        <span className="mt-0.5 text-gray-400">Count: {order.leadsReceived}</span>
                                                    </div>
                                                </td>

                                                {/* Status */}
                                                <td className="py-3 pr-4">
                                                    <div className="flex flex-col items-start gap-1">
                                                        <span className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${getStatusColor(order.status)}`}>
                                                            {STATUS_LABELS[order.status] || order.status}
                                                        </span>
                                                        {order.isAutoSuspended && (
                                                            <span className="inline-flex rounded-full bg-orange-100 px-2 py-1 text-xs font-semibold text-orange-800 dark:bg-orange-900 dark:text-orange-200">
                                                                ⏸ cap atteint
                                                            </span>
                                                        )}
                                                        {order.isAutoSuspended && blocking > 0 && (
                                                            <button
                                                                type="button"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    showLeads(order._id, counts.conflict > 0 ? 'conflict' : 'pending');
                                                                }}
                                                                title="Leads non tranchés sur une commande en cap atteint : voir les leads"
                                                                className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-1 text-xs font-semibold text-red-700 hover:bg-red-200 dark:bg-red-900 dark:text-red-200"
                                                            >
                                                                <MdWarning size={12} /> {blocking} à traiter
                                                            </button>
                                                        )}
                                                    </div>
                                                </td>

                                                {/* Date */}
                                                <td className="py-3 pr-4 text-sm text-gray-600 dark:text-gray-400">
                                                    {new Date(order.createdAt).toLocaleDateString('fr-FR')}
                                                </td>

                                                {/* Actions */}
                                                <td className="py-3" onClick={(e) => e.stopPropagation()}>
                                                    <div className="flex items-center gap-1">
                                                        <select
                                                            value={order.status}
                                                            onChange={(e) => handleChangeStatus(order, e.target.value as PurchaseOrder['status'])}
                                                            className="rounded border border-gray-300 bg-white px-2 py-1 text-xs dark:border-navy-600 dark:bg-navy-800 dark:text-white"
                                                        >
                                                            <option value="active">Active</option>
                                                            <option value="completed">Completed</option>
                                                            <option value="cancelled">Cancelled</option>
                                                            <option value="pending_payment">Pending Payment</option>
                                                        </select>
                                                        {order.isAutoSuspended && (
                                                            <button
                                                                title="Lever la suspension (cap atteint)"
                                                                onClick={() => handleLiftCapAtteint(order)}
                                                                className="rounded p-1 text-orange-600 hover:bg-orange-50 dark:text-orange-400 dark:hover:bg-navy-600"
                                                            >
                                                                <MdPlayArrow size={18} />
                                                            </button>
                                                        )}
                                                        <button
                                                            title="Delete"
                                                            onClick={() => handleDeletePurchaseOrder(order)}
                                                            className="rounded p-1 text-red-500 hover:bg-red-50 dark:text-red-400 dark:hover:bg-navy-600"
                                                        >
                                                            <MdDelete size={18} />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>

                                            {isExpanded && (
                                                <tr className="border-b border-gray-200 dark:border-navy-600">
                                                    <td colSpan={8} className="bg-gray-50 p-4 dark:bg-navy-900">
                                                        {renderLeadsPanel(order)}
                                                    </td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination */}
                {!loading && (
                    <div className="mt-5 flex items-center justify-between">
                        <div className="text-sm text-gray-600 dark:text-gray-400">
                            {total > 0 ? `Showing ${startItem}–${endItem} of ${total.toLocaleString()} purchase orders` : 'No purchase orders'}
                        </div>
                        {totalPages > 1 && (
                            <div className="flex items-center gap-1">
                                <button
                                    onClick={() => setPage(page - 1)}
                                    disabled={page === 1}
                                    className="flex items-center rounded-lg border border-gray-300 px-2 py-1.5 text-sm disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-100 dark:border-navy-600 dark:hover:bg-navy-700"
                                >
                                    <MdChevronLeft size={18} />
                                </button>
                                {getPageNumbers().map((p, i) =>
                                    p === '...' ? (
                                        <span key={`ellipsis-${i}`} className="px-2 text-sm text-gray-400">...</span>
                                    ) : (
                                        <button
                                            key={p}
                                            onClick={() => setPage(p)}
                                            className={`rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
                                                page === p
                                                    ? 'bg-navy-700 text-white dark:bg-brand-400'
                                                    : 'border border-gray-300 hover:bg-gray-100 dark:border-navy-600 dark:hover:bg-navy-700 dark:text-white'
                                            }`}
                                        >
                                            {p}
                                        </button>
                                    )
                                )}
                                <button
                                    onClick={() => setPage(page + 1)}
                                    disabled={page === totalPages}
                                    className="flex items-center rounded-lg border border-gray-300 px-2 py-1.5 text-sm disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-100 dark:border-navy-600 dark:hover:bg-navy-700"
                                >
                                    <MdChevronRight size={18} />
                                </button>
                            </div>
                        )}
                    </div>
                )}
            </Card>
        </div>
    );
}
